import { useCallback, useMemo, useState } from 'react';

import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useTitle } from 'react-use';
import { toast } from 'sonner';

import ContextualHelp from '@/components/atoms/ContextualHelp/ContextualHelp';
import SearchBar from '@/components/atoms/SearchBar/SearchBar';
import { EmptySkeleton } from '@/components/ui/custom/EmptySkeleton';
import { useEmptyStateView } from '@/hooks/useEmptyStateView';
import { useReportEmptyState } from '@/hooks/useReportEmptyState';
import { getPermissions } from '@/redux/permissions/selectors';
import { setSidebarContent } from '@/redux/sidebar/actions';
import { trackEvent } from '@/segment/segment';
import useSegments from '@/segment/useSegments';
import { actions } from '@/shared/types/permissions';
import { EventName, PageName } from '@/shared/types/segment';
import { RootState } from '@/shared/types/state';
import { API_KEYS_DOCS_URL } from '@/util/constants';

import { APIKeysTable } from './APIKeysTable';
import { EmptyAPIKeys } from './EmptyAPIKeys';
import { apiKeyTypeLabelMapper } from './helper';
import useCredentials from '../../hooks/useCredentials';
import { Credential, DEV_TOOLS_SIDEBAR_KEYS } from '../../types';

interface APIKeysProps {
  onEmptyStateChange: (isEmpty: boolean) => void;
}

export const APIKeys = ({ onEmptyStateChange }: APIKeysProps) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { credentials, listStatus, deleteCredential } = useCredentials();
  const [search, setSearch] = useState('');

  const canWrite = useSelector((state: RootState) =>
    getPermissions(state, 'credentials', actions.WRITE)
  );
  const canDelete = useSelector((state: RootState) =>
    getPermissions(state, 'credentials', actions.DELETE)
  );

  useTitle(t('pages.devtool_apikey'));

  const isLoading = listStatus === 'pending';
  const { showEmpty, showEmptySkeleton, showList, isEmptyPage } = useEmptyStateView({
    scope: 'credentials',
    isLoading,
    isEmpty: credentials.length === 0,
    isError: listStatus === 'error',
  });

  useReportEmptyState(isEmptyPage, onEmptyStateChange);

  useSegments(PageName.API_KEYS);

  const handleGenerate = useCallback(() => {
    dispatch(setSidebarContent(DEV_TOOLS_SIDEBAR_KEYS.apikeys));
  }, [dispatch]);

  const handleDelete = useCallback(
    (credential: Credential, onSettled: () => void) => {
      trackEvent(EventName.ClickDeleteAPIKey);
      deleteCredential(credential.credential_id, {
        onSuccess: () => {
          toast.success(t('developer_tools.credentials.deleted', { 0: credential.name }));
        },
        onError: (error) => {
          toast.error(error.message);
        },
        onSettled,
      });
    },
    [deleteCredential, t]
  );

  const filteredCredentials = useMemo(() => {
    if (!search.trim()) {
      return credentials;
    }
    const query = search.toLowerCase();
    return credentials.filter(
      (credential) =>
        credential.name.toLowerCase().includes(query) ||
        apiKeyTypeLabelMapper(credential.type, t).toLowerCase().includes(query)
    );
  }, [credentials, search, t]);

  return (
    <>
      <ContextualHelp
        title={t('developer_tools.contextual_help.api_keys_title')}
        name="about_api_keys"
        links={[{ label: t('common.learn_more'), url: API_KEYS_DOCS_URL }]}
      >
        {t('developer_tools.contextual_help.api_keys_message')}
      </ContextualHelp>

      {showEmpty && <EmptyAPIKeys canWrite={canWrite} onGenerate={handleGenerate} />}

      {showEmptySkeleton && <EmptySkeleton descriptionLines={3} />}

      {showList && (
        <>
          <div className="mb-4 pl-1">
            <SearchBar
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={t('developer_tools.api_keys_search')}
            />
          </div>

          <APIKeysTable
            credentials={filteredCredentials}
            isLoading={isLoading}
            canDelete={canDelete}
            onDelete={handleDelete}
          />
        </>
      )}
    </>
  );
};
